/**
 * TriggerEditor — right-aside editor for the trigger node.
 *
 * Sections:
 *  1. Trigger type selector (dropdown — lists all triggerDefs)
 *  2. Bot-Only Mode toggle (formerly "Override Routing")
 *  3. Routing Priority input (conversation_opened only) — lower = higher priority
 *  4. Channel filter (multi-select)
 *  5. Trigger-specific config (conditional — see TRIGGER CONFIG SECTIONS below)
 *  6. Filter Conditions (expression editor)
 *
 * TRIGGER CONFIG SECTIONS:
 *  Each case in renderTriggerConfig() handles one trigger type's unique options.
 *  Config is stored in node.triggerConfig (TriggerConfig type in types.tsx).
 *
 * AUDIT / DIAGNOSTICS:
 *  When the trigger type changes, a "TriggerTypeChanged" event is logged via
 *  useActivityLog so admins can diagnose why a workflow stopped firing.
 *  Log format: { workflowId, oldTrigger, newTrigger, changedAt }
 *
 * ADDING CONFIG FOR A NEW TRIGGER:
 *  1. Extend TriggerConfig in types.tsx with the new fields.
 *  2. Add a case in renderTriggerConfig() below.
 *  3. Document: what the field does, valid values, how the engine reads it.
 */
import Select from "@/components/Select";
import * as React from "react";
import { useMemo, useRef, useEffect, useState, useCallback } from "react";
import MultiSelect, { MultiValue } from "react-select";
import { triggerDefs, WorkflowNode, Trigger, TriggerConfig, getAfterBehavior } from "../types";
import { FormGroup, ToggleGroup } from "./RightAsideHelpers";
import { channelOptions } from "@/types";
import { ChevronDown, SelectOption } from "@velaro/velaro-component-lib";
import { ExpressionEditor } from "@/components/Expressions/ExpressionEditor";
import { Expression, SerializedCondition } from "@/components/Expressions/types";
import useActivityLog from "@/hooks/useActivityLog";
import useApi from "@/hooks/useApi";
import useSubscriptions from "@/hooks/useSubscriptions";
import useTrackEvent from "@/hooks/useTrackEvent";
import { useParams } from "react-router-dom";

// ── Deployment picker helpers ──────────────────────────────────────────────────

interface DeploymentOption {
  deploymentId: string;
  displayName: string;
}

/** Extract selected deploymentIds from the trigger expression (if any). */
function getDeploymentIdsFromExpression(expression: Expression): string[] {
  for (const andBlock of expression) {
    for (const cond of andBlock) {
      if (cond.type === "deployment") {
        try {
          const parsed = JSON.parse(cond.data) as { deploymentIds?: string[] };
          return parsed.deploymentIds ?? [];
        } catch {
          return [];
        }
      }
    }
  }
  return [];
}

/** Return expression with deployment condition replaced/added or removed. */
function setDeploymentInExpression(expression: Expression, ids: string[]): Expression {
  const deploymentCond: SerializedCondition = {
    type: "deployment",
    data: JSON.stringify({ deploymentIds: ids })
  };

  // Strip existing deployment conditions from all blocks
  const stripped: Expression = expression
    .map(block => block.filter(c => c.type !== "deployment"))
    .filter(block => block.length > 0);

  if (ids.length === 0) return stripped;

  // Prepend to first AND-block, or create a new one
  if (stripped.length === 0) return [[deploymentCond]];
  return [[deploymentCond, ...stripped[0]], ...stripped.slice(1)];
}

/**
 * Collapsible section for scoping a workflow to specific deployments.
 * Hidden entirely when the account has ≤1 deployment (no value in showing it).
 * Auto-opens when a deployment condition already exists (backward-compat).
 */
function MultiDeploymentSection({
  expression,
  onUpdateExpression
}: {
  expression: Expression;
  onUpdateExpression: (e: Expression) => void;
}) {
  const api = useApi();
  const [deployments, setDeployments] = useState<DeploymentOption[] | null>(null);

  useEffect(() => {
    api.messaging.get("Deployment").then(async res => {
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data)) {
          setDeployments(data.filter((d: DeploymentOption) => d.deploymentId).map((d: DeploymentOption) => ({
            deploymentId: d.deploymentId,
            displayName: d.displayName || d.deploymentId
          })));
        }
      }
    }).catch(() => setDeployments([]));
  }, [api.messaging]); // eslint-disable-line react-hooks/exhaustive-deps

  const selected = getDeploymentIdsFromExpression(expression);
  const [open, setOpen] = useState(selected.length > 0);

  const toggle = useCallback((id: string, checked: boolean) => {
    const next = checked ? [...selected, id] : selected.filter(s => s !== id);
    onUpdateExpression(setDeploymentInExpression(expression, next));
  }, [selected, expression, onUpdateExpression]);

  // Still loading — render nothing (avoids flash)
  if (deployments === null) return null;

  // Only one chat button: this setting has no meaning, hide it entirely
  if (deployments.length <= 1 && selected.length === 0) return null;

  return (
    <div className="mt-1">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 transition-colors"
        aria-expanded={open}
      >
        <span className="text-[10px]">{open ? "▼" : "▶"}</span>
        {selected.length > 0
          ? <span className="text-primary font-medium">Scoped to {selected.length} chat button{selected.length > 1 ? "s" : ""}</span>
          : <span>Scope to a specific chat button</span>
        }
      </button>
      {open && (
        <div className="mt-2 p-3 bg-slate-50 border border-slate-200 rounded-lg">
          <p className="text-xs text-slate-500 mb-2">
            Each widget you embed on a website has its own <strong>chat button</strong> with a unique ID (visible in the embed code under Deployments).
            By default, this workflow runs for <strong>all of your chat buttons</strong>.
            Check specific buttons below to run this workflow only when a visitor chats through that button —
            useful if you have multiple websites, brands, or landing pages each with their own widget.
          </p>
          <div className="space-y-1.5">
            <div className="flex flex-col gap-0.5 max-h-40 overflow-y-auto border border-slate-200 rounded-md p-2 bg-white">
              {deployments.map(d => (
                <label
                  key={d.deploymentId}
                  className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 rounded px-1 py-0.5"
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(d.deploymentId)}
                    onChange={e => toggle(d.deploymentId, e.target.checked)}
                    className="cursor-pointer accent-primary"
                  />
                  <span className="text-slate-700 flex-1 truncate">{d.displayName}</span>
                  <code className="text-[10px] font-mono text-slate-400 shrink-0">{d.deploymentId}</code>
                </label>
              ))}
            </div>
            {selected.length > 0 && (
              <p className="text-xs text-emerald-600 font-medium">
                ✓ Scoped to {selected.length} chat button{selected.length > 1 ? "s" : ""}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

interface Props {
  node: WorkflowNode;
  onUpdate: (node: Partial<WorkflowNode>) => void;
}

export default function TriggerEditor({ node, onUpdate }: Props) {
  const { id: workflowId } = useParams();
  const { track } = useActivityLog();
  const { track: trackEvent } = useTrackEvent();
  const { subscription } = useSubscriptions();
  const showV2Fields = subscription?.enableNewWorkflowsUX === true;
  const prevTriggerRef = useRef<Trigger | undefined>(node.trigger);

  // ── Audit: log trigger type changes ────────────────────────────────────────
  // Fires only when the trigger type actually changes (not on every render).
  useEffect(() => {
    const prev = prevTriggerRef.current;
    if (prev !== undefined && prev !== node.trigger) {
      track({
        category: "Workflows",
        action: "TriggerTypeChanged",
        entityType: "Workflow",
        entityId: workflowId ?? "unknown",
        detail: {
          oldTrigger: prev,
          newTrigger: node.trigger,
          changedAt: new Date().toISOString(),
        },
      });
    }
    prevTriggerRef.current = node.trigger;
  }, [node.trigger, workflowId, track]);

  // ── Channel multi-select value ─────────────────────────────────────────────
  const channelSelectValue = useMemo(() => {
    if (!node.channels || node.channels.length === 0) return [];
    return node.channels.map(
      (channel) =>
        channelOptions.find((option) => option.value === channel) ??
        channelOptions[0]
    );
  }, [node.channels]);

  function updateChannels(selectedOptions: MultiValue<SelectOption>) {
    onUpdate({
      channels: selectedOptions.map((option) => option.value.toString()),
    });
  }

  function updateConfig(partial: Partial<TriggerConfig>) {
    onUpdate({ triggerConfig: { ...(node.triggerConfig ?? {}), ...partial } });
  }

  // ── Trigger-specific config panels ─────────────────────────────────────────
  function renderTriggerConfig() {
    const cfg = node.triggerConfig ?? {};

    switch (node.trigger) {
      // ── CSAT rating filter ──────────────────────────────────────────────────
      // Engine: fires only when CsatResponse.Rating is between [min, max].
      // If both are null the trigger fires for all ratings.
      case "csat_submitted":
        return (
          <FormGroup
            label="Rating Filter"
            tooltipText="Only fire this workflow when the CSAT rating is within this range. Leave both empty to fire for any rating. Useful for service recovery (1–2) or review requests (5)."
          >
            <div className="flex items-center gap-2 text-sm">
              <div className="flex flex-col gap-1 flex-1">
                <label className="text-xs text-gray-500">Min rating</label>
                <select
                  value={cfg.csatMinRating ?? ""}
                  onChange={(e) =>
                    updateConfig({ csatMinRating: e.target.value ? Number(e.target.value) : undefined })
                  }
                  aria-label="Minimum CSAT rating"
                  className="border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:border-primary-hover"
                >
                  <option value="">Any</option>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <option key={n} value={n}>{n} star{n !== 1 ? "s" : ""}</option>
                  ))}
                </select>
              </div>
              <span className="mt-5 text-gray-400">–</span>
              <div className="flex flex-col gap-1 flex-1">
                <label className="text-xs text-gray-500">Max rating</label>
                <select
                  value={cfg.csatMaxRating ?? ""}
                  onChange={(e) =>
                    updateConfig({ csatMaxRating: e.target.value ? Number(e.target.value) : undefined })
                  }
                  aria-label="Maximum CSAT rating"
                  className="border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:border-primary-hover"
                >
                  <option value="">Any</option>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <option key={n} value={n}>{n} star{n !== 1 ? "s" : ""}</option>
                  ))}
                </select>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Example: Min 1 – Max 2 fires only for unhappy customers. Min 5 – Max 5 fires only for promoters.
            </p>
          </FormGroup>
        );

      // ── Label filter ───────────────────────────────────────────────────────
      // Engine: fires when a label in labelIds[] is applied to the conversation.
      // Empty labelIds = fire for any label.
      case "label_applied":
        return (
          <FormGroup
            label="Which Labels"
            tooltipText="Only fire when one of these labels is applied. Leave empty to fire for any label. Create labels under Settings → Conversation Labels."
          >
            <LabelIdsInput
              value={cfg.labelIds ?? []}
              onChange={(ids) => updateConfig({ labelIds: ids })}
            />
          </FormGroup>
        );

      // ── Campaign filter ────────────────────────────────────────────────────
      // Engine: fires when any broadcast email is opened / any link is clicked.
      // broadcastId scopes to a specific campaign (optional).
      case "email_opened":
      case "email_link_clicked":
        return (
          <FormGroup
            label="Campaign Scope"
            tooltipText="Leave empty to fire for any broadcast campaign. Enter a campaign ID to scope this trigger to a specific email campaign."
          >
            <div className="flex items-center gap-2">
              <input
                type="number"
                placeholder="All campaigns"
                value={cfg.broadcastId ?? ""}
                onChange={(e) =>
                  updateConfig({ broadcastId: e.target.value ? Number(e.target.value) : undefined })
                }
                aria-label="Broadcast campaign ID (optional)"
                className="flex-1 border border-gray-200 rounded px-3 py-1.5 text-sm focus:outline-none focus:border-primary-hover"
              />
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Find the campaign ID on the Campaigns page. Leave blank = any campaign fires this workflow.
            </p>
          </FormGroup>
        );

      // ── SLA threshold ──────────────────────────────────────────────────────
      // Engine: fires when the conversation wait time reaches this many minutes.
      case "sla_breach":
        return (
          <FormGroup
            label="SLA Threshold (minutes)"
            tooltipText="Fire this workflow when a conversation has been waiting this long without an agent response. Set lower than your SLA target to trigger before breach."
          >
            <input
              type="number"
              min={1}
              max={1440}
              placeholder="e.g. 10"
              value={cfg.slaThresholdMinutes ?? ""}
              onChange={(e) =>
                updateConfig({ slaThresholdMinutes: e.target.value ? Number(e.target.value) : undefined })
              }
              aria-label="SLA threshold in minutes"
              className="w-full border border-gray-200 rounded px-3 py-1.5 text-sm focus:outline-none focus:border-primary-hover"
            />
            <p className="text-xs text-gray-400 mt-1">
              Tip: Set to 5 minutes before your SLA target so you can act before breaching, not after.
            </p>
          </FormGroup>
        );

      // ── Tier escalation filter ─────────────────────────────────────────────
      // Engine: fires when routing advances to tierNumber (0 = any tier).
      case "tier_escalated":
        return (
          <FormGroup
            label="Tier Number"
            tooltipText="Fire only when routing escalates to this specific tier. Set to 0 (or leave empty) to fire on any tier escalation."
          >
            <input
              type="number"
              min={0}
              max={10}
              placeholder="Any tier"
              value={cfg.tierNumber ?? ""}
              onChange={(e) =>
                updateConfig({ tierNumber: e.target.value ? Number(e.target.value) : undefined })
              }
              aria-label="Tier number"
              className="w-full border border-gray-200 rounded px-3 py-1.5 text-sm focus:outline-none focus:border-primary-hover"
            />
            <p className="text-xs text-gray-400 mt-1">
              Example: 2 = only fires when Tier 1 is skipped and Tier 2 agents are being tried.
            </p>
          </FormGroup>
        );

      // ── Schedule (cron) ────────────────────────────────────────────────────
      // Engine: Hangfire reads scheduleExpression as a cron and fires the workflow.
      // All times are UTC.
      case "scheduled":
        return (
          <>
            <FormGroup
              label="Cron Expression (UTC)"
              tooltipText="Standard 5-part cron: minute hour day-of-month month day-of-week. Examples: '0 9 * * 1' = Mon 9am, '0 0 1 * *' = 1st of month midnight. All times in UTC."
            >
              <input
                type="text"
                placeholder="e.g. 0 9 * * 1"
                value={cfg.scheduleExpression ?? ""}
                onChange={(e) => updateConfig({ scheduleExpression: e.target.value })}
                aria-label="Cron expression"
                className="w-full border border-gray-200 rounded px-3 py-1.5 text-sm font-mono focus:outline-none focus:border-primary-hover"
              />
              <div className="mt-1 grid grid-cols-2 gap-1">
                {CRON_PRESETS.map((preset) => (
                  <button
                    key={preset.label}
                    type="button"
                    onClick={() => updateConfig({ scheduleExpression: preset.value })}
                    aria-label={`Set cron: ${preset.label}`}
                    className="text-[11px] text-left px-2 py-1 rounded bg-gray-50 hover:bg-primary-bg hover:text-primary border border-gray-200 transition-colors"
                  >
                    {preset.label}
                    <span className="block font-mono text-[10px] text-gray-400">{preset.value}</span>
                  </button>
                ))}
              </div>
            </FormGroup>
            <FormGroup
              label="Timezone"
              tooltipText="The cron is evaluated in UTC. Display/reference timezone for admin readability only."
            >
              <input
                type="text"
                placeholder="UTC (default)"
                value={cfg.scheduleTimezone ?? ""}
                onChange={(e) => updateConfig({ scheduleTimezone: e.target.value })}
                aria-label="Schedule timezone"
                className="w-full border border-gray-200 rounded px-3 py-1.5 text-sm focus:outline-none focus:border-primary-hover"
              />
            </FormGroup>
          </>
        );

      // ── Deal stage filter ──────────────────────────────────────────────────
      // Engine: fires when CRM deal stage name matches (case-insensitive). Empty = any.
      case "deal_stage_changed":
        return (
          <FormGroup
            label="Stage Name (optional)"
            tooltipText="Only fire when the deal transitions to this specific stage name. Match the exact stage name in HubSpot or Dynamics. Leave empty to fire on any stage change."
          >
            <input
              type="text"
              placeholder="e.g. Proposal Sent"
              value={cfg.dealStage ?? ""}
              onChange={(e) => updateConfig({ dealStage: e.target.value })}
              aria-label="Deal stage name"
              className="w-full border border-gray-200 rounded px-3 py-1.5 text-sm focus:outline-none focus:border-primary-hover"
            />
          </FormGroup>
        );

      // ── Order status filter ────────────────────────────────────────────────
      // Engine: fires when BigCommerce/Shopify order status matches.
      case "order_status_changed":
        return (
          <FormGroup
            label="Order Status (optional)"
            tooltipText="Only fire for a specific order status. Common values: pending, shipped, delivered, refunded, cancelled. Leave empty to fire for any status change."
          >
            <select
              value={cfg.orderStatus ?? ""}
              onChange={(e) => updateConfig({ orderStatus: e.target.value || undefined })}
              aria-label="Order status"
              className="w-full border border-gray-200 rounded px-3 py-1.5 text-sm focus:outline-none focus:border-primary-hover"
            >
              <option value="">Any status change</option>
              <option value="pending">Pending</option>
              <option value="awaiting_payment">Awaiting Payment</option>
              <option value="awaiting_fulfillment">Awaiting Fulfillment</option>
              <option value="shipped">Shipped</option>
              <option value="awaiting_pickup">Awaiting Pickup</option>
              <option value="partially_shipped">Partially Shipped</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
              <option value="refunded">Refunded</option>
              <option value="disputed">Disputed</option>
            </select>
          </FormGroup>
        );

      // ── Webhook shared secret ──────────────────────────────────────────────
      // Engine: POST /api/webhooks/{workflowId} validates X-Velaro-Webhook-Key.
      case "webhook_received":
        return (
          <FormGroup
            label="Webhook Key"
            tooltipText="A shared secret that external systems must include as the X-Velaro-Webhook-Key header when calling your workflow webhook URL. Keep this secret — it authenticates the caller."
          >
            <input
              type="text"
              placeholder="e.g. whk_abc123xyz"
              value={cfg.webhookKey ?? ""}
              onChange={(e) => updateConfig({ webhookKey: e.target.value })}
              aria-label="Webhook key"
              className="w-full border border-gray-200 rounded px-3 py-1.5 text-sm font-mono focus:outline-none focus:border-primary-hover"
            />
            <p className="text-xs text-gray-400 mt-1">
              Webhook URL (after saving): <span className="font-mono">/api/webhooks/&#123;workflowId&#125;</span>
              <br />Include this key as <span className="font-mono">X-Velaro-Webhook-Key</span> in your POST.
            </p>
          </FormGroup>
        );

      default:
        return null;
    }
  }

  // ── What data is available for this trigger ────────────────────────────────
  function renderTriggerHelp() {
    const help = TRIGGER_HELP[node.trigger];
    if (!help) return null;
    return (
      <div className="mt-1 mb-3 p-3 bg-blue-50 border border-blue-100 rounded-lg text-xs text-blue-700 space-y-1.5">
        <div className="font-semibold text-blue-800">About this trigger</div>
        <p>{help.description}</p>
        {help.availableData.length > 0 && (
          <div>
            <span className="font-semibold">Available data:</span>{" "}
            {help.availableData.join(", ")}
          </div>
        )}
        {help.tip && (
          <div className="border-t border-blue-200 pt-1.5">
            <span className="font-semibold">Tip: </span>{help.tip}
          </div>
        )}
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <FormGroup
        label="Trigger"
        tooltipText="A Trigger is the event that starts your workflow. Changing this will be logged for diagnostics."
      >
        <div className="relative">
          <Select<Trigger>
            className="pl-3 pr-10 appearance-none focus:outline-none"
            options={triggerDefs}
            value={node.trigger}
            onChange={(v) => onUpdate({ trigger: v })}
          />
          <div className="absolute right-1 top-1/2 transform -translate-y-1/2 pointer-events-none">
            <ChevronDown className="h-7 w-7 text-gray-300" />
          </div>
        </div>
      </FormGroup>

      {/* Contextual help box per trigger */}
      {renderTriggerHelp()}

      {showV2Fields ? (
        <>
          <FormGroup
            label="After this workflow…"
            tooltipText="What routing happens when this workflow finishes running."
          >
            <div className="flex flex-col gap-2 mt-1">
              {(["routing", "bot-end", "agent-handoff"] as const).map((opt) => {
                const labels: Record<string, string> = {
                  "routing": "Continue with default routing",
                  "bot-end": "Bot ends the conversation (no agent)",
                  "agent-handoff": "Force hand off to an agent"
                };
                const descs: Record<string, string> = {
                  "routing": "Default — routing rules and team assignments apply normally.",
                  "bot-end": "The workflow keeps the conversation; no agent is ever assigned.",
                  "agent-handoff": "Force agent routing regardless of workflow content."
                };
                const current = getAfterBehavior(node);
                return (
                  <label key={opt} className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="afterBehavior"
                      value={opt}
                      checked={current === opt}
                      onChange={() => {
                        onUpdate({ afterBehavior: opt } as Partial<WorkflowNode>);
                        trackEvent("setting_change", `Workflows/AfterBehavior/${opt}`);
                      }}
                      className="mt-0.5 accent-primary"
                    />
                    <div>
                      <span className="text-sm font-medium text-slate-700">{labels[opt]}</span>
                      <p className="text-xs text-slate-400 mt-0.5">{descs[opt]}</p>
                    </div>
                  </label>
                );
              })}
            </div>
          </FormGroup>
          <FormGroup
            label="Run mode"
            tooltipText="Exclusive: this workflow is the first-match winner — only it runs. Always: this workflow also runs if its conditions match, even after another exclusive workflow already fired."
          >
            <div className="flex gap-3 mt-1">
              {(["exclusive", "always"] as const).map((mode) => {
                const current = node.runMode ?? "exclusive";
                return (
                  <label key={mode} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="runMode"
                      value={mode}
                      checked={current === mode}
                      onChange={() => {
                        onUpdate({ runMode: mode } as Partial<WorkflowNode>);
                        trackEvent("setting_change", `Workflows/RunMode/${mode}`);
                        track({
                          category: "Workflows",
                          action: "WorkflowRunModeChanged",
                          entityType: "Workflow",
                          entityId: workflowId ?? "unknown",
                          detail: { from: current, to: mode }
                        });
                      }}
                      className="accent-primary"
                    />
                    <span className="text-sm text-slate-700 capitalize">{mode}</span>
                  </label>
                );
              })}
            </div>
            <p className="text-xs text-slate-400 mt-1.5">
              {(node.runMode ?? "exclusive") === "always"
                ? "Runs after the exclusive winner if conditions match."
                : "First-match by priority — only one exclusive workflow runs per event."}
            </p>
          </FormGroup>
        </>
      ) : (
        <FormGroup
          label="After Workflow"
          tooltipText="What happens when this workflow finishes. OFF (default): agent assignment fires — if your workflow has a Transfer To Agent or Transfer To Team node, that handles it. If not, the conversation falls through to your default team. ON: the conversation stays with the bot, no agent is ever assigned. Tip: the cleanest way to control which agent or team receives the conversation is to end your workflow with a Transfer To Agent node."
        >
          <ToggleGroup
            toggled={node.overrideRouting || false}
            label={node.overrideRouting ? "Stay with bot — skip agent routing" : "Hand off to agent routing (default)"}
            changeToggle={() =>
              onUpdate({ overrideRouting: !node.overrideRouting })
            }
          />
        </FormGroup>
      )}

      {node.trigger === "conversation_opened" && (
        <>
          <FormGroup
            label="Routing Priority"
            tooltipText="When multiple conversation_opened workflows have conditions that all match, the one with the lowest priority number runs first. Use a lower number (e.g. 10) for specific-condition workflows that should win over a general fallback (e.g. 100)."
          >
            <input
              type="number"
              min={1}
              max={9999}
              className="w-24 border border-gray-200 rounded px-2 py-1 text-sm"
              value={node.routingPriority ?? 100}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                if (!isNaN(val) && val >= 1) {
                  onUpdate({ routingPriority: val });
                }
              }}
            />
            <span className="text-xs text-gray-400 mt-1 block">Lower = higher priority. Default: 100.</span>
          </FormGroup>

          <MultiDeploymentSection
            expression={node.triggerExpression ?? []}
            onUpdateExpression={(expression) => onUpdate({ triggerExpression: expression })}
          />
        </>
      )}

      <FormGroup
        label="Channel"
        tooltipText="Choose where you want this trigger to work. Features may vary based on the channel."
      >
        <div className="cursor-pointer">
          <MultiSelect
            isMulti
            styles={{
              control: (styles) => ({
                ...styles,
                cursor: "pointer",
                border: "1px solid #E2E8F0",
              }),
            }}
            className="basic-multi-select rounded-lg"
            placeholder="All channels"
            options={channelOptions}
            onChange={updateChannels}
            value={channelSelectValue}
          />
        </div>
      </FormGroup>

      {/* Trigger-specific config — only shown when relevant */}
      {renderTriggerConfig()}

      <FormGroup
        label="Conditions"
        tooltipText="Optional: narrow down when this workflow runs using attribute, channel, URL, or time-of-day conditions."
      >
        <ExpressionEditor
          expressionType="use-workflow"
          expression={node.triggerExpression}
          onUpdate={(expression) => onUpdate({ triggerExpression: expression })}
        />
      </FormGroup>
    </>
  );
}

// ── Cron presets ──────────────────────────────────────────────────────────────
const CRON_PRESETS = [
  { label: "Every day at 9am UTC",   value: "0 9 * * *" },
  { label: "Mon–Fri at 8am UTC",     value: "0 8 * * 1-5" },
  { label: "Every Monday 9am UTC",   value: "0 9 * * 1" },
  { label: "1st of month midnight",  value: "0 0 1 * *" },
  { label: "Every hour",             value: "0 * * * *" },
  { label: "Every 15 minutes",       value: "*/15 * * * *" },
];

// ── Trigger help content ──────────────────────────────────────────────────────
// Shown in a blue info box below the trigger selector.
// Keep this in sync with TriggerModal.tsx examples.
interface TriggerHelp {
  description: string;
  availableData: string[];
  tip?: string;
}

const TRIGGER_HELP: Partial<Record<string, TriggerHelp>> = {
  conversation_opened: {
    description: "Fires the instant a new conversation is created — before any agent sees it.",
    availableData: ["conversationId", "visitorId", "channel", "pageUrl", "contactAttributes"],
    tip: "Combine with a channel condition to run different workflows for chat vs SMS vs WhatsApp.",
  },
  conversation_resolved: {
    description: "Fires after a conversation is marked resolved. The full transcript is available at this point.",
    availableData: ["conversationId", "agentId", "teamId", "channel", "resolvedAt", "transcript"],
    tip: "Use the Push to CRM action node to sync the transcript to HubSpot or Dynamics at resolve time.",
  },
  csat_submitted: {
    description: "Fires when a visitor submits a CSAT survey. Filter by rating range to build targeted responses (service recovery for low scores, referral asks for high scores).",
    availableData: ["conversationId", "rating", "comment", "agentId", "teamId", "channel"],
    tip: "Rating 1–2 → service recovery workflow. Rating 5 → ask for a Google / G2 review.",
  },
  email_opened: {
    description: "Fires the first time a recipient opens one of your campaign emails. The tracking pixel fires on image load.",
    availableData: ["broadcastId", "recipientId", "contactEmail", "openedAt"],
    tip: "Scope to a specific campaign ID to avoid triggering on unrelated emails.",
  },
  email_link_clicked: {
    description: "Fires when a tracked link in a campaign email is clicked and redirected through Velaro.",
    availableData: ["broadcastId", "recipientId", "contactEmail", "clickedUrl", "clickedAt"],
    tip: "Use a Condition node to branch on which URL was clicked (pricing vs demo vs docs).",
  },
  scheduled: {
    description: "Fires on a repeating cron schedule (UTC). Useful for daily/weekly batch operations that don't depend on a live conversation.",
    availableData: ["scheduledAt", "siteId"],
    tip: "Use the HTTP Request action to call your own API or trigger external webhooks on a schedule.",
  },
  webhook_received: {
    description: "Fires when an external system POSTs to your unique webhook URL with the correct key. The request body is available as workflow variables.",
    availableData: ["webhookPayload", "receivedAt", "callerIp"],
    tip: "Use Postman or curl to test your webhook before wiring it to production systems.",
  },
  sla_breach: {
    description: "Fires when a conversation has been waiting longer than your configured threshold. Set this BELOW your SLA target so you can intervene before it's too late.",
    availableData: ["conversationId", "waitTimeMinutes", "teamId", "agentId"],
    tip: "Chain: SLA trigger → Condition (has agent assigned?) → No: auto-route / Yes: escalate to supervisor.",
  },
  label_applied: {
    description: "Fires when an agent applies a label. Select which labels trigger this workflow in the config below.",
    availableData: ["conversationId", "labelId", "labelName", "agentId"],
    tip: "Create labels like 'VIP', 'Bug', 'Upsell Opportunity' and wire each to a different workflow.",
  },
};

// ── LabelIdsInput ─────────────────────────────────────────────────────────────
// Simple comma-separated label ID input — replace with a proper multi-select
// once the labels endpoint is wired to this component.
function LabelIdsInput({
  value,
  onChange,
}: {
  value: number[];
  onChange: (ids: number[]) => void;
}) {
  const [text, setText] = React.useState(value.join(", "));

  function commit(raw: string) {
    const ids = raw
      .split(",")
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => !isNaN(n) && n > 0);
    onChange(ids);
  }

  return (
    <div>
      <input
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={(e) => commit(e.target.value)}
        placeholder="Label IDs, comma-separated (e.g. 1, 5, 12)"
        aria-label="Label IDs"
        className="w-full border border-gray-200 rounded px-3 py-1.5 text-sm focus:outline-none focus:border-primary-hover"
      />
      <p className="text-xs text-gray-400 mt-1">
        Find label IDs under Settings → Conversation Labels. Leave empty to fire for any label.
      </p>
    </div>
  );
}
